import { BaseTextFieldBox } from "@/shared/components/text-box/base-text-box";
import React, { useState } from "react";
import { AutoCompleteContentList, AutoCompleteContents, } from "@/shared/components/auto-complete/auto-complete";
import { useDebounce } from "@/shared/hooks/debounce/use-debounce";
import { SuggestionDomainNameList } from "@/shared/config/globals";
import { useInputValidation } from "@/shared/hooks/use-input-validation";

interface Props {
  id: string;
  className?: string;
  onChange?: (newValue: string) => void;
  title?: string;
  initialValue?: string;
  placeholder?: string;
  nextFocusElementId?: string;
  errorMessage?: string;
  isDisabled?: boolean; // disabled指定
  isReadOnly?: boolean; // readOnly指定
  // ブラウザ/パスワードマネージャーの自動入力制御。
  // デフォルトは "off"（自動入力させない）。ログイン画面のみ "username" を指定する。
  autoComplete?: string;
}

export const MailAddressForm = (props: Props) => {
  const {
    id = "",
    className = "",
    onChange = () => { /**intentionally empty */},
    title = "メールアドレス",
    initialValue = "",
    placeholder = "メールアドレスを入力",
    nextFocusElementId = "",
    errorMessage = "",
    isDisabled = false,
    isReadOnly = false,
    autoComplete = "off",
  } = props;

  // メールアドレス入力値
  const [value, setValue] = useState(initialValue ?? "");

  // メールアドレスのテキストボックスにフォーカスが当たっているかどうか
  const [isFocusTitleField, setIsFocusTitleField] = useState<boolean>(false);

  // IME入力中かどうか
  const [isComposing, setIsComposing] = useState<boolean>(false);

  // フォーカス用index
  const [currentTabindex, setCurrentTabindex] = useState<number | null>(null);

  // オートコンプリート用リスト
  const [autoCompleteItemList, setAutoCompleteItemList] = useState<string[]>(
    []
  );

  const { validate } = useInputValidation("halfWidthAlnumSymbols");

  const applyInputValue = (rawValue: string, composing = isComposing) => {
    const formattedValue = validate(rawValue, composing);
    setValue(formattedValue);
    onChange(formattedValue);
  };

  // メールアドレス変更
  const onChangeMailAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyInputValue(e.target.value);
  };

  // テキストボックスから外れる際の処理
  const onBlurTextBox = (event: React.FocusEvent<HTMLInputElement>) => {
    // blurの際に、次にfocusされる要素を取得
    const relatedTarget = event.relatedTarget as Element;
    // focusされる要素のid名を取得
    const relatedTargetId = relatedTarget?.getAttribute("id");

    // 自分自身のelementIdの後ろに表示されるオートコンプリートのインデックスがつく正規表現パターンを準備
    const regexPattern = new RegExp(`^${id}_[0-9]+$`);

    // NOTE: 次のフォーカス先のidに自分自身のelementIdが含まれているか（自分自身かどうか）
    // かつ、自分自身が使用するオートコンプリート部品かどうか
    if (
      relatedTargetId?.includes(id) &&
      regexPattern.test(relatedTargetId)
    ) {
      // オートフォーカスのリストを選択している場合、何もしない
      return;
    } else {
      // テキストボックス外の要素にフォーカスしている場合、リストを非表示
      resetAutoCompleteField();
    }
  };

  // フォーカスする
  const doFocus = (id: string) => {
    document.getElementById(id)?.focus();
  };

  // オートコンプリート ********************************************************** //
  // オートコンプリート用リスト作成
  const convertToAutoCompleteContentList = (
    items: string[]
  ): AutoCompleteContentList[] => {
    const autoCompleteContentList: AutoCompleteContentList[] = [];

    items.map((item, index) => {
      autoCompleteContentList.push({
        id: index.toString(),
        mainText: "",
        subText: item,
        suffixText: "",
      });
    });
    return autoCompleteContentList;
  };

  // オートコンプリート用リスト更新
  const updateAutoCompleteItemList = (value: string) => {
    // ”@”の位置のindex
    const atSignIndex = value.indexOf("@");
    // ”@”以降の1文字目
    const strNextAtSign = value.substring(atSignIndex + 1);

    if (value.match("@") && strNextAtSign) {
      const domainName = value.substring(atSignIndex);

      // 入力したドメイン名を含む候補のリスト
      const domainNameSuggestionList = SuggestionDomainNameList.filter((item) =>
        item.includes(domainName)
      );

      if (domainNameSuggestionList[0] === domainName) {
        // 候補のリスト項目と完全に一致している(=入力済)場合はサジェストを消す
        setAutoCompleteItemList([]);
      } else {
        setAutoCompleteItemList(domainNameSuggestionList);
      }
    } else {
      setAutoCompleteItemList([]);
    }
  };

  // オートコンプリート項目選択時の処理
  const selectAutoCompleteContent = (index: number) => {
    if (index > autoCompleteItemList.length) {
      // リストに存在しない場合は、リセットして何もしない
      return;
    }

    // "@"より前の文字列
    const strPreviousAtSign = value.substring(0, value.indexOf("@"));
    // "@"を含むドメイン名
    const selectItem = autoCompleteItemList[index];
    // セットするメールアドレス
    const email = strPreviousAtSign + selectItem;

    // メールアドレスを変更
    applyInputValue(email);
  };

  // オートコンプリート機能をリセット(リストの非表示)
  const resetAutoCompleteField = () => {
    // リアルタイム検索フラグをOFF
    setIsFocusTitleField(false);
    // 補完リストの初期化
    setAutoCompleteItemList([]);
    // フォーカス対象のリストindexの初期化
    setCurrentTabindex(null);
  };

  // オートコンプリートリスト選択時のキーボード入力イベント
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowDown") {
      if (
        currentTabindex !== null &&
        currentTabindex >= autoCompleteItemList.length - 1
      ) {
        // オートコンプリートのリストの最下にフォーカスが当たっている場合は何もしない
        return;
      }

      // フォーカス先のID取得
      const nextTabindex = getNextTabindex(currentTabindex);
      const nextID = getAutoCompleteFiledElementID(nextTabindex, id);
      // フォーカスする
      doFocus(nextID);
      setCurrentTabindex(nextTabindex);
    }

    if (event.key === "ArrowUp") {
      if (currentTabindex === null) {
        // テキストボックスにフォーカスが当たっている場合は何もしない
        return;
      }

      // フォーカス先のID取得
      const previousIndex = getPreviousTabindex(currentTabindex);
      const previousID = getAutoCompleteFiledElementID(
        previousIndex,
        id
      );

      // 対象のIDの要素にフォーカスする
      doFocus(previousID);
      setCurrentTabindex(previousIndex);
    }

    if (event.key === "Enter") {
      if (currentTabindex !== null) {
        selectAutoCompleteContent(currentTabindex);
        resetAutoCompleteField();
      }
    }

    if (event.key === "Tab") {
      // NOTE:クリック以外で他にフォーカスが移った場合の考慮
      resetAutoCompleteField();
    }
  };

  // テキストボックスフォーカス時イベント
  const onFocusTextBox = () => {
    setIsFocusTitleField(true);
    updateAutoCompleteItemList(value);
  };

    // リアルタイム検索(オートコンプリート機能)
  // 連続する入力が一旦止まってから、最後のコールバックを実行するまでの待ち時間(ms)
  const waitTimeMS = 300;
  useDebounce(
    () => {
      if (isFocusTitleField) {
        updateAutoCompleteItemList(value);// 次にフォーカスしたい部品のid
        if (nextFocusElementId) {
          doFocus(nextFocusElementId);
        }
      } else {
        // 次にフォーカスしたい部品のid
        if (nextFocusElementId) {
          doFocus(nextFocusElementId);
        }
      }
    },
    waitTimeMS,
    [value]
  );

  // 次のフォーカス先のindexを取得
  const getNextTabindex = (currentTabindex: number | null) => {
    if (currentTabindex === null) {
      return 0;
    } else {
      return currentTabindex + 1;
    }
  };

  // 前のフォーカス先のindexを取得
  const getPreviousTabindex = (currentTabindex: number) => {
    if (currentTabindex === 0) {
      return null;
    } else {
      return currentTabindex - 1;
    }
  };

  // indexからIDを取得
  const getAutoCompleteFiledElementID = (
    index: number | null,
    idPrefix?: string
  ) => {
    if (index === null) {
      return idPrefix ?? "";
    } else {
      return `${idPrefix}_${index}`;
    }
  };


  return (
    <div
      className="mail-address-form"
      onKeyDown={onKeyDown}
    >
      <BaseTextFieldBox
        id={id}
        className={className}
        title={title}
        placeholder={placeholder}
        value={value}
        type={"text"}
        inputMode="email"
        onChange={onChangeMailAddress}
        onFocus={onFocusTextBox}
        onBlur={onBlurTextBox}
        errorMessage={errorMessage}
        labelEnable={true}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        maxLength={100}
        autoComplete={autoComplete}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => {
          setIsComposing(false);
          applyInputValue(value, false);
        }}
      />
      <div
        className={`mail-address-form__auto-complete mh16${
          errorMessage ? " error" : ""
        }`}
      >
{/**        <AutoCompleteContents/>*/} 
      </div>
    </div>
  );
};

//        <AutoCompleteContents
//          autoCompleteContentList={convertToAutoCompleteContentList(
//            autoCompleteItemList
//          )}
//          onMouseDownContent={selectAutoCompleteContent}
//          prefixIDName={id}
//          resetAutoCompleteContentList={resetAutoCompleteField}
//        />
// デザインがないため一旦外す。実装は要確認